import React, { useState, useEffect } from 'react';
import axios from 'axios';

// Função para garantir que a primeira letra de cada palavra seja maiúscula
const capitalizeFirstLetters = (text) => {
    return text
        .split(' ') // Divide as palavras
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()) // Maiúscula na primeira letra
        .join(' '); // Junta de volta as palavras com espaço
};

export default function ManageCategoryModal({ onClose, onCategoryAction, onUpdateScreen }) {
    const [action, setAction] = useState('create'); // 'create', 'delete' ou 'update'
    const [categoryName, setCategoryName] = useState('');
    const [categories, setCategories] = useState([]); // Lista de categorias
    const [selectedCategory, setSelectedCategory] = useState('');

    // Função para carregar as categorias existentes, chamada apenas uma vez ao abrir o modal
    const loadCategories = async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL || "https://api.clelton-churrasqueiras.site"}/categoryRoutes/category`);
            setCategories(response.data);
        } catch (error) {
            console.error('Erro ao carregar as categorias', error);
        }
    };

    // useEffect para carregar categorias apenas uma vez ao abrir o modal
    useEffect(() => {
        loadCategories(); // Carrega as categorias quando o modal é aberto
    }, []); // O array vazio garante que o efeito seja executado apenas uma vez, no momento da montagem do componente

    // Função para criar uma categoria
    const handleCreateCategory = async () => {
        if (!categoryName) {
            alert('Por favor, informe o nome da categoria');
            return;
        }

        const formattedCategoryName = capitalizeFirstLetters(categoryName); // Capitaliza o nome da categoria

        try {
            await axios.post(`${process.env.REACT_APP_API_URL || "https://api.clelton-churrasqueiras.site"}/categoryRoutes/category`, { nome: formattedCategoryName });
            onCategoryAction(); // Atualiza a tela após a criação
            onUpdateScreen();
            onClose(); // Fecha o modal
        } catch (error) {
            console.error('Erro ao criar a categoria:', error);
        }
    };

    // Função para excluir uma categoria
    const handleDeleteCategory = async () => {
        if (!selectedCategory) {
            alert('Por favor, selecione uma categoria para excluir');
            return;
        }

        try {
            await axios.delete(`${process.env.REACT_APP_API_URL || "https://api.clelton-churrasqueiras.site"}/categoryRoutes/category/${selectedCategory}`);
            onCategoryAction(); // Atualiza a tela após a exclusão
            onUpdateScreen();
            onClose(); // Fecha o modal
        } catch (error) {
            console.error('Erro ao excluir a categoria:', error);
        }
    };

    // Função para atualizar o nome da categoria
    const handleUpdateCategory = async () => {
        if (!selectedCategory || !categoryName) {
            alert('Por favor, selecione uma categoria e informe o novo nome');
            return;
        }

        const formattedCategoryName = capitalizeFirstLetters(categoryName); // Capitaliza o nome da categoria

        try {
            await axios.put(`${process.env.REACT_APP_API_URL || "https://api.clelton-churrasqueiras.site"}/categoryRoutes/category/${selectedCategory}`, {
                nome: formattedCategoryName,
            });
            onCategoryAction(); // Atualiza a tela após a atualização
            onUpdateScreen();
            onClose(); // Fecha o modal
        } catch (error) {
            console.error('Erro ao atualizar a categoria:', error);
        }
    };

    return (
        <div className="fixed inset-0 flex justify-center items-center bg-black/70 backdrop-blur-md z-50 p-4">
            <div className="glass-panel p-8 w-full max-w-md bg-slate-900/90 shadow-2xl border-white/10 relative">
                <div className="text-center mb-8">
                    <h2 className="text-2xl font-bold text-gray-200">Gerenciar Categorias</h2>
                </div>

                <div className="mb-6">
                    <label className="text-sm text-gray-400 block mb-2">Escolha uma ação:</label>
                    <div className="flex space-x-4 bg-white/5 p-2 rounded-xl border border-white/10">
                        <label className="flex items-center text-gray-300 cursor-pointer hover:text-white transition-colors">
                            <input
                                type="radio"
                                name="action"
                                value="create"
                                checked={action === 'create'}
                                onChange={() => setAction('create')}
                                className="mr-2 accent-blue-500"
                            />
                            Criar
                        </label>
                        <label className="flex items-center text-gray-300 cursor-pointer hover:text-white transition-colors">
                            <input
                                type="radio"
                                name="action"
                                value="delete"
                                checked={action === 'delete'}
                                onChange={() => setAction('delete')}
                                className="mr-2 accent-blue-500"
                            />
                            Excluir
                        </label>
                        <label className="flex items-center text-gray-300 cursor-pointer hover:text-white transition-colors">
                            <input
                                type="radio"
                                name="action"
                                value="update"
                                checked={action === 'update'}
                                onChange={() => setAction('update')}
                                className="mr-2 accent-blue-500"
                            />
                            Atualizar
                        </label>
                    </div>
                </div>

                {/* Formulário de Criação */}
                {action === 'create' && (
                    <div className="animate-fade-in">
                        <div className="mb-6">
                            <label className="text-sm text-gray-400">Nome da Categoria</label>
                            <input
                                type="text"
                                value={categoryName}
                                onChange={(e) => setCategoryName(e.target.value)}
                                className="glass-input w-full mt-2"
                                placeholder="Ex: Bebidas"
                            />
                        </div>
                        <button
                            onClick={handleCreateCategory}
                            className="glass-button w-full shadow-[0_0_15px_rgba(59,130,246,0.5)]"
                        >
                            Criar Categoria
                        </button>
                    </div>
                )}

                {/* Dropdown para Exclusão ou Atualização */}
                {(action === 'delete' || action === 'update') && (
                    <div className="animate-fade-in">
                        <div className="mb-6">
                            <label className="text-sm text-gray-400">
                                {action === 'delete' ? 'Selecione a Categoria para Excluir' : 'Selecione a Categoria para Atualizar'}
                            </label>
                            <select
                                value={selectedCategory}
                                onChange={(e) => setSelectedCategory(e.target.value)}
                                className="glass-select w-full mt-2"
                            >
                                <option value="" disabled>Selecione uma categoria</option>
                                {categories.map((category) => (
                                    <option key={category.id} value={category.id}>
                                        {category.nome}
                                    </option>
                                ))}
                            </select>
                        </div>
                        {action === 'update' && (
                            <div className="mb-6">
                                <label className="text-sm text-gray-400">Novo Nome da Categoria</label>
                                <input
                                    type="text"
                                    value={categoryName}
                                    onChange={(e) => setCategoryName(e.target.value)}
                                    className="glass-input w-full mt-2"
                                    placeholder="Ex: Novo Nome"
                                />
                            </div>
                        )}
                        <button
                            onClick={action === 'delete' ? handleDeleteCategory : handleUpdateCategory}
                            className={action === 'delete' ? 'glass-button-danger w-full' : 'glass-button w-full shadow-[0_0_15px_rgba(59,130,246,0.5)]'}
                        >
                            {action === 'delete' ? 'Excluir Categoria' : 'Atualizar Categoria'}
                        </button>
                    </div>
                )}

                {/* Botões de Fechar ou Confirmar */}
                <div className="flex justify-between space-x-4 mt-8">
                    <button
                        onClick={onClose}
                        className="glass-button bg-gray-600 hover:bg-gray-700 w-full"
                    >
                        Fechar
                    </button>
                    <button
                        onClick={onClose}
                        className="glass-button-danger w-full"
                    >
                        Cancelar
                    </button>
                </div>
            </div>
        </div>
    );
}
